<script setup>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '@/config';

// Reactive data
const workOrders = ref([]);
const filteredWorkOrders = ref([]);
const loading = ref(false);
const error = ref(null);
const notification = ref({
  show: false,
  message: '',
  type: ''
});

// Filter options
const filterOptions = ref({
  fromDate: '',
  toDate: ''
});

// API endpoints
const workOrdersApiUrl = `${API_BASE_URL}/api/workorders/list`;
const workTypesApiUrl = `${API_BASE_URL}/api/worktypes`;
const workTradesApiUrl = `${API_BASE_URL}/api/worktrades`;

// Dropdown data for display
const workTypes = ref([]);
const workTrades = ref([]);

// Fetch display data for work types and trades
const fetchDisplayData = async () => {
  try {
    const [typesRes, tradesRes] = await Promise.all([
      axios.get(workTypesApiUrl),
      axios.get(workTradesApiUrl)
    ]);

    workTypes.value = typesRes.data;
    workTrades.value = tradesRes.data;
  } catch (err) {
    console.error('Error fetching display data:', err);
  }
};

// Fetch open work orders (no filters)
const fetchOpenWorkOrders = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const response = await axios.get(workOrdersApiUrl);
    workOrders.value = response.data.filter(wo => wo.WorkStatus === 1);
    filteredWorkOrders.value = [...workOrders.value];
    
    if (workOrders.value.length === 0) {
      showNotification('No open work orders found.', 'info');
    }
  } catch (err) {
    console.error('Error fetching open work orders:', err);
    error.value = 'Failed to load work orders.';
    if (err.response?.status === 503) {
      error.value = 'Database service unavailable.';
    } else {
      error.value = 'Server Error fetching work orders.';
    }
    showNotification(error.value, 'error');
    workOrders.value = [];
    filteredWorkOrders.value = [];
  } finally {
    loading.value = false;
  }
};

// Apply date filters on Start Date
const applyDateFilters = () => {
  if (!filterOptions.value.fromDate && !filterOptions.value.toDate) {
    filteredWorkOrders.value = [...workOrders.value];
    return;
  }

  filteredWorkOrders.value = workOrders.value.filter(wo => {
    const startDate = new Date(wo.StartDate);
    let fromCondition = true;
    let toCondition = true;

    if (filterOptions.value.fromDate) {
      const fromDate = new Date(filterOptions.value.fromDate);
      fromCondition = startDate >= fromDate;
    }

    if (filterOptions.value.toDate) {
      const toDate = new Date(filterOptions.value.toDate);
      toDate.setHours(23, 59, 59, 999); // Include the entire end date
      toCondition = startDate <= toDate;
    }

    return fromCondition && toCondition;
  });
};

// Clear all filters
const clearFilters = () => {
  filterOptions.value.fromDate = '';
  filterOptions.value.toDate = '';
  filteredWorkOrders.value = [...workOrders.value];
};

// Watch for date filter changes
watch([() => filterOptions.value.fromDate, () => filterOptions.value.toDate], () => {
  applyDateFilters();
});

// Show notification helper
const showNotification = (message, type) => {
  notification.value.message = message;
  notification.value.type = type;
  notification.value.show = true;
  setTimeout(() => {
    notification.value.show = false;
  }, 5000);
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date)) return '-';
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString(undefined, options);
};

// Format date for input fields (YYYY-MM-DD)
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date)) return '';
  return date.toISOString().split('T')[0];
};

// Get status badge class
const getStatusBadgeClass = (statusId) => {
  const statusMap = {
    1: 'bg-secondary', // New
    2: 'bg-primary',   // In Progress
    3: 'bg-warning',   // On Hold
    4: 'bg-info',      // Waiting for Parts
    5: 'bg-success',   // Completed
    6: 'bg-danger'     // Cancelled
  };
  return statusMap[statusId] || 'bg-secondary';
};

// Get status text
const getStatusText = (statusId) => {
  const statusMap = {
    1: 'New',
    2: 'In Progress',
    3: 'On Hold',
    4: 'Waiting for Parts',
    5: 'Completed',
    6: 'Cancelled'
  };
  return statusMap[statusId] || 'Unknown';
};

// Get work type description
const getWorkTypeDescription = (workTypeId) => {
  const workType = workTypes.value.find(wt => wt.WorkTypeID === workTypeId);
  return workType ? workType.WorkTypeDescription : workTypeId;
};

// Get work trade description
const getWorkTradeDescription = (workTradeId) => {
  const workTrade = workTrades.value.find(wt => wt.WorkTradeID === workTradeId);
  return workTrade ? workTrade.WorkTrade : workTradeId;
};

// Export to Excel
const exportToExcel = () => {
  if (filteredWorkOrders.value.length === 0) {
    showNotification('No work orders to export.', 'warning');
    return;
  }

  const dataToExport = filteredWorkOrders.value.map(wo => ({
    'WO No': wo.WorkorderNo,
    'Asset No': wo.AssetNo,
    'Location': wo.LocationNo,
    // 'Date Received': formatDate(wo.DateReceived),
    'ActDateStart' : formatDate(wo.ActDateStart),
    'ActDateEnd' : formatDate(wo.ActDateEnd),
    'Work Type': getWorkTypeDescription(wo.WorkType),
    // 'Start Date': formatDate(wo.StartDate),
    'Work Trade': getWorkTradeDescription(wo.WOTrade),
    'PM No': wo.PMNo || '-',
    'Task No': wo.TaskNo || '-',
    'Site Code': wo.SiteCode
  }));

  const worksheet = XLSX.utils.json_to_sheet(dataToExport);
  
  // Auto-size columns
  const maxWidth = dataToExport.reduce((w, r) => Math.max(w, Object.values(r).map(v => v ? v.toString().length : 0).reduce((a, b) => Math.max(a, b), 0)), 10);
  worksheet['!cols'] = Array(Object.keys(dataToExport[0] || {}).length).fill({ width: maxWidth });
  
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Open Work Orders');

  const fileName = `OpenWorkOrders_${filterOptions.value.fromDate || 'all'}_to_${filterOptions.value.toDate || 'all'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(workbook, fileName);
  showNotification(`Exported ${filteredWorkOrders.value.length} work orders to Excel!`, 'success');
};

// Print PDF
// Print PDF
const printPDF = () => {
  if (filteredWorkOrders.value.length === 0) {
    showNotification('No data to print.', 'warning');
    return;
  }
  
  // Store original display data for print
  const printData = {
    title: 'Closed Work Orders Report',
    filters: {
      fromDate: filterOptions.value.fromDate ? formatDate(filterOptions.value.fromDate) : 'All Dates',
      toDate: filterOptions.value.toDate ? formatDate(filterOptions.value.toDate) : 'All Dates'
    },
    workOrders: filteredWorkOrders.value,
    workTypes: workTypes.value,
    workTrades: workTrades.value
  };
  
  // Store in session storage for print window to access
  sessionStorage.setItem('printData', JSON.stringify(printData));
  
  // Open print window
  const printWindow = window.open('', '_blank');
  
  // Get the base URL for logo paths
  const baseUrl = window.location.origin;
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Open Work Orders Report</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .print-header { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          margin-bottom: 30px; 
          border-bottom: 2px solid #333; 
          padding-bottom: 10px; 
        }
        .logo-left { 
          flex: 1; 
          text-align: left; 
        }
        .logo-right { 
          flex: 1; 
          text-align: right; 
        }
        .logo-img { 
          max-height: 80px; 
          max-width: 150px; 
        }
        .logo-img2 { 
          max-height: 120px; 
          max-width: 120px; 
        }
        .header-center { 
          flex: 2; 
          text-align: center; 
        }
        .print-title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
        .print-filters { margin-bottom: 20px; font-size: 14px; color: #666; }
        .print-table { width: 100%; border-collapse: collapse; font-size: 10px; }
        .print-table th, .print-table td { border: 1px solid #ddd; padding: 6px; text-align: left; }
        .print-table th { background-color: #f5f5f5; font-weight: bold; }
        .print-table tr:nth-child(even) { background-color: #f9f9f9; }
        .badge { padding: 3px 6px; border-radius: 3px; font-size: 9px; font-weight: bold; }
        .badge-success { background-color: #28a745; color: white; }
        .badge-danger { background-color: #dc3545; color: white; }
        @media print {
          body { margin: 0; }
          .print-header { margin-bottom: 20px; }
          .logo-img { max-height: 70px; max-width: 120px; }
          .logo-img2 { max-height: 150px; max-width: 120px; }
          .print-table { font-size: 8px; }
          .print-table th, .print-table td { padding: 3px; }
        }
      </style>
    </head>
    <body>
      <!-- Header with Two Logos -->
      <div class="print-header">
        <div class="logo-left">
          <!-- Left Logo - Replace with your actual logo path -->
          <img src="../logo.png" alt="Company Logo Left" class="logo-img">
          <!-- Alternative: Use base64 encoded image or CDN URL -->
          <!-- <img src="https://via.placeholder.com/150x80/007bff/ffffff?text=LOGO+LEFT" alt="Logo Left" class="logo-img"> -->
        </div>
        <div class="header-center">
          <div class="print-title">Closed Work Orders Report</div>
          <div class="print-filters">
              Report Date: ${new Date().toLocaleDateString()} | 
            Total Records: ${printData.workOrders.length}
          </div>
        </div>
        <div class="logo-right">
          <!-- Right Logo - Replace with your actual logo path -->
          <img src="../logo2.png" alt="Company Logo Right" class="logo-img2" style="height = 150px">
          <!-- Alternative: Use base64 encoded image or CDN URL -->
          <!-- <img src="https://via.placeholder.com/150x80/28a745/ffffff?text=LOGO+RIGHT" alt="Logo Right" class="logo-img"> -->
        </div>
      </div>

      <table class="print-table">
        <thead>
          <tr>
            <th>WO No</th>
            <th>Asset No</th>
            <th>Location</th>
            
            <th>Work Type</th>
           
          
            <th>Actual Start Date</th>
            <th>Actual End Date</th>
            <th>Work Trade</th>
            
            <th>Site Code</th>
          </tr>
        </thead>
        <tbody>
          ${printData.workOrders.map(wo => {
            const statusClass = {
              5: 'badge-success',
              6: 'badge-danger'
            }[wo.WorkStatus] || 'badge-secondary';
            
            return `
            <tr>
              <td><strong>${wo.WorkorderNo}</strong></td>
              <td>${wo.AssetNo}</td>
              <td>${wo.LocationNo}</td>
              
              <td>${getWorkTypeDescription(wo.WorkType)}</td>
              
             
              <td>${formatDate(wo.ActDateStart)}</td>
              <td>${formatDate(wo.ActDateEnd)}</td>
              <td>${getWorkTradeDescription(wo.WOTrade)}</td>
             
              <td>${wo.SiteCode}</td>
            </tr>
            `;
          }).join('')}
        </tbody>
      </table>
      <script>
        window.onload = function() {
          window.print();
          setTimeout(() => window.close(), 500);
        };
      <\/script>
    </body>
    </html>
  `);
  printWindow.document.close();
};

// Initialize component
onMounted(() => {
  fetchDisplayData();
  fetchOpenWorkOrders();
});
</script>

<template>
  <div class="container mt-4">
    
    <!-- Print Header -->
    <div class="print-only-header">
      <div class="print-date">Report Date: {{ formatDate(new Date()) }}</div>
    </div>
    
    <h2 class="report-title">Open Work Orders Report</h2>

    <!-- Start Date Filters -->
    <div class="card mb-4 print-hidden-element">
      <div class="card-header">
        <h5 class="card-title mb-0">Date Filter</h5>
      </div>
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label">From Date</label>
            <input 
              type="date" 
              class="form-control" 
              v-model="filterOptions.fromDate"
              :max="filterOptions.toDate || new Date().toISOString().split('T')[0]"
            >
          </div>
          <div class="col-md-3">
            <label class="form-label">To Date</label>
            <input 
              type="date" 
              class="form-control" 
              v-model="filterOptions.toDate"
              :min="filterOptions.fromDate"
              :max="new Date().toISOString().split('T')[0]"
            >
          </div>
          <div class="col-md-6 d-flex align-items-end gap-2">
            <button @click="clearFilters" class="btn btn-outline-secondary">
              Clear Filters
            </button>
            <div class="text-muted ms-2">
              Showing {{ filteredWorkOrders.length }} of {{ workOrders.length }} work orders
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="d-flex justify-content-between align-items-center mb-4 print-hidden-element">
      <div class="text-muted">
        Showing {{ filteredWorkOrders.length }} work order(s)
        <span v-if="filterOptions.fromDate || filterOptions.toDate" class="ms-2">
          (Filtered by Start Date: 
          <span v-if="filterOptions.fromDate">From {{ formatDate(filterOptions.fromDate) }}</span>
          <span v-if="filterOptions.fromDate && filterOptions.toDate"> to </span>
          <span v-if="filterOptions.toDate">To {{ formatDate(filterOptions.toDate) }}</span>)
        </span>
      </div>
      <div class="report-actions d-flex gap-2" v-if="filteredWorkOrders.length > 0 && !loading">
        <button @click="exportToExcel" class="btn btn-success">
          <i class="bi bi-file-earmark-spreadsheet"></i> Export To Excel
        </button>
        <button @click="printPDF" class="btn btn-secondary">
          <i class="bi bi-printer"></i> Print as PDF
        </button>
      </div>
    </div>

    <!-- Loading & Error States -->
    <div v-if="loading" class="alert alert-info print-hidden-element">
      <i class="bi bi-arrow-repeat spinner"></i> Loading work orders...
    </div>
    <div v-if="error" class="alert alert-danger print-hidden-element">
      {{ error }}
    </div>
    <div v-if="notification.show" :class="['alert', `alert-${notification.type}`, 'print-hidden-element']">
      {{ notification.message }}
    </div>

    <!-- Work Orders Table -->
    <div v-if="!loading && !error">
      <div v-if="filteredWorkOrders.length > 0" class="table-responsive">
        <table class="table table-striped table-hover report-table">
          <thead>
            <tr>
              <th>WO No</th>
              <th>Asset No</th>
              <th>Location</th>
              <!-- <th>Date Received</th> -->
              <th>Work Type</th>
              <!-- <th>Start Date</th> -->
              <th>Actual Start Date</th>
              <th>Actual End Date</th>
              <th>Work Trade</th>
              <th>Site Code</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="wo in filteredWorkOrders" :key="wo.id">
              <td>
                <strong>{{ wo.WorkorderNo }}</strong>
              </td>
              <td>{{ wo.AssetNo }}</td>
              <td>{{ wo.LocationNo }}</td>
              <!-- <td>{{ formatDate(wo.DateReceived) }}</td> -->
              <td>
                {{ getWorkTypeDescription(wo.WorkType) }}
              </td>
              <!-- <td>{{ formatDate(wo.StartDate) }}</td> -->
              <td>{{ formatDate(wo.ActDateStart) }}</td>
              <td>{{ formatDate(wo.ActDateEnd) }}</td>
              <td>
                {{ getWorkTradeDescription(wo.WOTrade) }}
              </td>
              <td>{{ wo.SiteCode }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="!loading">
        <div class="alert alert-warning text-center">
          <i class="bi bi-inbox"></i><br>
          <span v-if="filterOptions.fromDate || filterOptions.toDate">
            No work orders found for the selected start date range.
          </span>
          <span v-else>
            No open work orders found.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.print-only-header {
  display: none;
}

.badge {
  font-size: 0.75em;
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Print Styles */
@media print {
  #app-header-area,
  .app-header,
  .navbar,
  .print-hidden-element,
  .report-actions,
  .alert,
  .badge {
    display: none !important;
  }

  .print-only-header {
    display: block !important;
    text-align: center;
    margin-bottom: 20px;
  }

  .print-date {
    font-size: 10pt;
  }

  .report-title {
    display: block !important;
    text-align: center;
    margin-top: 20px;
    margin-bottom: 30px;
    font-size: 18pt;
    color: black !important;
  }

  .container {
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 0 10px;
    box-shadow: none;
  }

  .table-responsive {
    overflow: visible !important;
  }

  .report-table {
    font-size: 9pt;
    width: 100% !important;
    table-layout: fixed;
    border-collapse: collapse;
  }

  .report-table th,
  .report-table td {
    padding: 4px;
    border: 1px solid #ccc;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .table-striped tbody tr:nth-of-type(odd) {
    background-color: #f2f2f2 !important;
  }
}
</style>